import { useMemo } from 'react';

import { Permission } from '@/api/permissions';
import { useGetUserProfile } from '@/api/user-dashboard/queries';
import { getPermissionSet } from '@/utils/permissions';

const EMPTY_PERMISSIONS: readonly Permission[] = [];

export type UsePermissionsResult = {
  /**
   * Permissions granted to the current session. Empty while the profile is
   * loading and empty on error — never partially populated.
   */
  permissions: readonly Permission[];
  /**
   * The profile carrying the permission list has not resolved yet — either it has never
   * loaded, or a cached list is being replaced and has not come back.
   */
  isLoading: boolean;
  /** The profile request failed; the permission list is unknown. */
  isError: boolean;
  /** O(1) membership check. `false` whenever the list is unknown. */
  has: (_permission: Permission) => boolean;
};

export const usePermissions = (): UsePermissionsResult => {
  const { user, isPending, isFetching, isInvalidated, isError } = useGetUserProfile();

  const isUnresolved = isPending || (isFetching && isInvalidated);
  const permissions = isError || isUnresolved ? undefined : user?.permissions;

  return useMemo(() => {
    const granted = getPermissionSet(permissions);

    return {
      permissions: permissions ?? EMPTY_PERMISSIONS,
      isLoading: isUnresolved,
      isError,
      has: (permission: Permission) => granted.has(permission),
    };
  }, [permissions, isUnresolved, isError]);
};

/**
 * Whether the current session holds `permission`.
 *
 * Returns `false` while the profile is loading and on error. Consumers render
 * the non-privileged view in that case — never a blocked or error state.
 */
export const useCan = (permission: Permission): boolean => {
  const { has } = usePermissions();

  return has(permission);
};
